import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { PublicationsRepositoryService } from '../../services/publications-repository.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PageScrollingService } from '@shared/services/page-scrolling';
import {
  BehaviorSubject,
  distinctUntilKeyChanged,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-publications-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, CommonModule],
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @Output() closeModal = new EventEmitter();

  public repository = inject(PublicationsRepositoryService);
  private pageScrolling = inject(PageScrollingService);
  private ngUnsubscribe$ = new Subject<void>();

  contentQuery$ = new BehaviorSubject<string | null>(null);
  publisherNameQuery$ = new BehaviorSubject<string | null>(null);

  ngOnInit(): void {
    this.setContentQuery();
    this.setPublisherNameQuery();
  }

  searchByContent(): void {
    this.contentQuery$
      .pipe(
        take(1),
        tap((content: string | null) =>
          this.repository.setQueryParamsUrl({ content })
        )
      )
      .subscribe();
  }

  searchByPublisher(): void {
    this.publisherNameQuery$
      .pipe(
        take(1),
        tap((publisher: string | null) =>
          this.repository.setQueryParamsUrl({ publisher })
        )
      )
      .subscribe();
  }

  changeValueContent(value: string | null): void {
    this.contentQuery$.next(value);

    if (!value) {
      this.repository.setQueryParamsUrl({ content: null });
    }
  }

  changeValuePublisher(value: string | null): void {
    this.publisherNameQuery$.next(value);

    if (!value) {
      this.repository.setQueryParamsUrl({ publisher: null });
    }
  }

  searchMobileByContent(): void {
    this.searchByContent();
    this.closeMobileModal();
    this.scrollToTop();
  }

  searchMobileByPublisher(): void {
    this.searchByPublisher();
    this.closeMobileModal();
    this.scrollToTop();
  }

  private closeMobileModal(): void {
    this.pageScrolling.togglePageScroll();
    this.closeModal.emit();
  }

  private scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  private setContentQuery(): void {
    this.repository.filters$
      .pipe(
        distinctUntilKeyChanged('contentQuery'),
        tap(({ contentQuery }) => this.contentQuery$.next(contentQuery)),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private setPublisherNameQuery(): void {
    this.repository.filters$
      .pipe(
        distinctUntilKeyChanged('publisherNameQuery'),
        tap(({ publisherNameQuery }) =>
          this.publisherNameQuery$.next(publisherNameQuery)
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
